import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ISpProfilePublic } from '@brushfire/types'
import {
  ACCOUNT_DEMOGRAPHIC_GRID_ROWS,
  displayValueForField,
  type AccountFieldKey,
  type AccountFieldRow,
} from '@/features/account/lib/account-display-utils'
import {
  accountContentColumnSx,
  accountDemographicCellFirstRowSx,
  accountDemographicCellSx,
  accountDemographicGridSx,
  accountDemographicIconOffsetSx,
  accountDemographicIconWrapSx,
  accountDemographicLabelValueGapSx,
  accountDemographicValueMutedSx,
  accountDemographicValuePrimarySx,
  accountFieldLabelSx,
  accountFieldValueSx,
  accountNotProvidedSx,
} from '@/features/account/lib/account-settings-tokens'

interface Props {
  profile: ISpProfilePublic
}

const PRIMARY_VALUE_KEYS = new Set<AccountFieldKey>(['gender', 'maritalStatus'])

const MUTED_VALUE_KEYS = new Set<AccountFieldKey>([
  'dateOfBirth',
  'city',
  'householdSize',
  'numberOfChildren',
  'industry',
])

const DARK_VALUE_KEYS = new Set<AccountFieldKey>(['employment', 'education', 'housingStatus'])

function valueTypographySx(key: AccountFieldKey, displayText: string) {
  if (displayText === 'Not provided') return accountNotProvidedSx
  if (DARK_VALUE_KEYS.has(key) || (key === 'employment' && displayText === '-')) {
    return accountFieldValueSx
  }
  if (PRIMARY_VALUE_KEYS.has(key)) return accountDemographicValuePrimarySx
  if (MUTED_VALUE_KEYS.has(key)) return accountDemographicValueMutedSx
  return accountDemographicValuePrimarySx
}

function DemographicCell({
  row,
  profile,
  firstRow = false,
}: Readonly<{
  row: AccountFieldRow
  profile: ISpProfilePublic
  firstRow?: boolean
}>) {
  const value = displayValueForField(profile, row.key)
  const displayText = value ?? (row.key === 'employment' ? '-' : 'Not provided')
  const Icon = row.icon
  const cellSx = firstRow ? accountDemographicCellFirstRowSx : accountDemographicCellSx

  return (
    <Box sx={cellSx} data-testid={`account-field-${row.key}`}>
      <Box
        sx={{
          ...accountDemographicIconWrapSx,
          ...(firstRow ? undefined : accountDemographicIconOffsetSx),
        }}
        aria-hidden
      >
        <Icon size={24} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={accountFieldLabelSx}>{row.label}</Typography>
        <Typography
          sx={{
            ...valueTypographySx(row.key, displayText),
            ...accountDemographicLabelValueGapSx,
          }}
        >
          {displayText}
        </Typography>
      </Box>
    </Box>
  )
}

export function DemographicFieldList({ profile }: Readonly<Props>) {
  return (
    <Box sx={accountContentColumnSx} data-testid="account-demographic-list">
      <Box sx={accountDemographicGridSx}>
        {ACCOUNT_DEMOGRAPHIC_GRID_ROWS.flatMap(([left, right], rowIndex) => [
          <DemographicCell
            key={left.key}
            row={left}
            profile={profile}
            firstRow={rowIndex === 0}
          />,
          <DemographicCell
            key={right.key}
            row={right}
            profile={profile}
            firstRow={rowIndex === 0}
          />,
        ])}
      </Box>
    </Box>
  )
}
